import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/utils';

export const iconColorClass = {
  current: '',
  foreground: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  destructive: 'text-destructive',
  accent: 'text-accent-foreground',
} as const;

export type IconColor = keyof typeof iconColorClass;

export type IconA11yProps =
  | {
      'aria-label': string;
      'aria-labelledby'?: never;
      'aria-hidden'?: never;
      title?: never;
    }
  | {
      'aria-labelledby': string;
      'aria-label'?: never;
      'aria-hidden'?: never;
      title?: never;
    }
  | {
      title: string;
      'aria-label'?: never;
      'aria-labelledby'?: never;
      'aria-hidden'?: never;
    }
  | {
      'aria-hidden': true;
      'aria-label'?: never;
      'aria-labelledby'?: never;
      title?: never;
    };

export type ArticIconProps = Omit<
  ComponentProps<'svg'>,
  'color' | 'aria-label' | 'aria-labelledby' | 'aria-hidden' | 'role' | 'viewBox'
> & {
  color?: IconColor;
} & IconA11yProps;

type ShellProps = ArticIconProps & {
  viewBox: string;
  children: ReactNode;
};

export function ArticIconShell({
  color = 'current',
  className,
  title,
  viewBox,
  children,
  ...props
}: ShellProps) {
  const hasTitle = title !== undefined && title !== '';
  const labelled =
    props['aria-label'] !== undefined || props['aria-labelledby'] !== undefined || hasTitle;

  return (
    <svg
      width="1em"
      height="1em"
      viewBox={viewBox}
      fill="none"
      data-slot="icon"
      role={labelled ? 'img' : undefined}
      className={cn(iconColorClass[color], className)}
      {...props}
    >
      {hasTitle ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}
